import React, { useState, useRef, useEffect } from "react";
import { Pencil, Check, X, Trash2 } from "lucide-react";
import CommunityAvatar from "../shared/CommunityAvatar";
import {
  deleteComment,
  getInitials,
  normalizeEmail,
  isValidAuthorDisplayName,
  resolveCommentAuthorName,
  updateComment,
} from "../../../data/communityHub";
import { timeAgo } from "../../../utils/communityHub";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";
import CommunityConfirmDialog from "../shared/CommunityConfirmDialog";

const EDIT_WINDOW_MS = 60_000;

export default function CommunityCommentItem({
  comment,
  currentUserEmail,
  hubId,
  postId,
  isSuperAdminUser,
  canModerateComments = false,
  editWindowMs = EDIT_WINDOW_MS,
  showRowHover = true,
}: {
  comment: any;
  currentUserEmail: string;
  hubId: string;
  postId: string;
  isSuperAdminUser: boolean;
  canModerateComments?: boolean;
  editWindowMs?: number;
  showRowHover?: boolean;
}) {
  const qc = useQueryClient();
  const createdAtSeconds = comment.createdAt?.seconds ?? 0;
  const isOwn =
    normalizeEmail(comment.author?.email) === normalizeEmail(currentUserEmail);
  const [isEditing, setIsEditing] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [editText, setEditText] = useState(comment.text);
  const [canEdit, setCanEdit] = useState(false);
  const editRef = useRef(null) as any;

  const storedName = String(comment.author?.name || "").trim();
  const [authorDisplayName, setAuthorDisplayName] = useState(
    isValidAuthorDisplayName(storedName) ? storedName : "",
  );
  const authorInitials = getInitials(authorDisplayName || "?");
  const authorAvatarUrl =
    comment.author?.photoURL || comment.author?.avatarUrl;

  useEffect(() => {
    let cancelled = false;
    resolveCommentAuthorName(comment.author).then((name) => {
      if (!cancelled) setAuthorDisplayName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [comment.author?.name, comment.author?.email]);

  useEffect(() => {
    if (!isOwn || !createdAtSeconds) {
      setCanEdit(false);
      return;
    }
    const createdMs = createdAtSeconds * 1000;
    const remaining = createdMs + editWindowMs - Date.now();
    if (remaining <= 0) {
      setCanEdit(false);
      return;
    }
    setCanEdit(true);
    const timer = setTimeout(() => {
      setCanEdit(false);
      setIsEditing(false);
    }, remaining);
    return () => clearTimeout(timer);
  }, [isOwn, createdAtSeconds, editWindowMs]);

  useEffect(() => {
    if (isEditing && editRef.current) {
      editRef.current.focus();
      editRef.current.selectionStart = editRef.current.value.length;
    }
  }, [isEditing]);

  const canDelete = isSuperAdminUser || canModerateComments || isOwn;

  const handleSaveEdit = async () => {
    if (!isOwn) return;
    const trimmed = editText.trim();
    if (!trimmed || trimmed === comment.text) {
      setIsEditing(false);
      setEditText(comment.text);
      return;
    }
    try {
      await updateComment(hubId, postId, comment.id, trimmed);
      qc.invalidateQueries(["hubPostComments", postId, hubId]);
      setIsEditing(false);
    } catch {
      toast.error("Failed to update comment");
    }
  };

  const handleDelete = async () => {
    if (!canDelete) return;
    try {
      await deleteComment(hubId, postId, comment.id);
      qc.invalidateQueries(["hubPostComments", postId, hubId]);
      toast.success("Comment deleted");
    } catch {
      toast.error("Failed to delete comment");
      throw new Error("delete failed");
    }
  };

  const rowClass = showRowHover
    ? "flex gap-3 group rounded-lg px-3 py-2.5 -mx-3 transition-colors duration-150 hover:bg-gray-50"
    : "flex gap-3 group";

  return (
    <>
    <div className={rowClass}>
      <CommunityAvatar
        initials={authorInitials}
        name={authorDisplayName}
        avatarUrl={authorAvatarUrl}
        size={32}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-semibold text-sm text-[#15415E]">
            {authorDisplayName}
          </span>
          <span className="text-xs text-gray-400">
            &middot; {createdAtSeconds ? timeAgo(createdAtSeconds) : ""}
          </span>
          {(canEdit || canDelete) && !isEditing && (
            <span className="inline-flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
              {canEdit && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-[#15415E]"
                  title="Edit (available for 1 min)"
                >
                  <Pencil size={13} />
                </button>
              )}
              {canDelete && (
                <button
                  type="button"
                  onClick={() => setDeleteConfirmOpen(true)}
                  className="p-0.5 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete comment"
                  aria-label="Delete comment"
                >
                  <Trash2 size={13} />
                </button>
              )}
            </span>
          )}
        </div>
        {isEditing ? (
          <div className="mt-1">
            <textarea
              ref={editRef}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSaveEdit();
                }
                if (e.key === "Escape") {
                  setIsEditing(false);
                  setEditText(comment.text);
                }
              }}
              className="w-full bg-gray-50 border border-[#15415E]/30 rounded-lg px-3 py-1.5 text-sm text-gray-700 focus:outline-none focus:border-[#15415E] focus:ring-1 focus:ring-[#15415E]/20 resize-none"
              rows={2}
            />
            <div className="flex items-center gap-1.5 mt-1">
              <button
                type="button"
                onClick={handleSaveEdit}
                className="p-1 rounded text-green-600 hover:bg-green-50 transition-colors"
              >
                <Check size={14} />
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsEditing(false);
                  setEditText(comment.text);
                }}
                className="p-1 rounded text-red-500 hover:bg-red-50 transition-colors"
              >
                <X size={14} />
              </button>
              <span className="text-[11px] text-gray-400 ml-1">
                Enter to save &middot; Esc to cancel
              </span>
            </div>
          </div>
        ) : (
          <p className="text-sm text-[#4B5563] mt-0.5 leading-relaxed whitespace-pre-line">
            {comment.text}
          </p>
        )}
      </div>
    </div>

    {deleteConfirmOpen && (
      <CommunityConfirmDialog
        title="Delete comment?"
        message="This comment will be permanently removed. This action cannot be undone."
        confirmLabel="Delete comment"
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleDelete}
      />
    )}
    </>
  );
}
